import { apiClient, apiClientEnvelope } from '@/lib/api-client';

/**
 * Ops simulation T13 — inventory report (GET /products/reports/inventory) and
 * min/max stock per product per location (PATCH /products/:id/stock-levels).
 * Quantities are stock units, not money.
 */

export interface InventoryBatch {
  batch_number: string;
  expiry_date: string;
  quantity: number;
}

export interface InventoryReportRow {
  product_id: string;
  name: string;
  sku: string;
  unit: string;
  location_id: string;
  location_name: string;
  parent_product_id: string | null;
  parent_name: string | null;
  variant_attributes: Record<string, string> | null;
  on_hand: number;
  batches: InventoryBatch[];
  serials: string[];
  untracked_quantity: number;
  incoming_po: number;
  incoming_transfers: number;
  incoming: number;
  outgoing_sales: number;
  outgoing_transfers: number;
  outgoing: number;
  planned_outgoing: number;
  forecast: number;
  avg_daily_sales: number;
  days_of_stock: number | null;
  min_stock: number;
  max_stock: number | null;
  stock_status: 'BELOW_MIN' | 'ABOVE_MAX' | 'OK';
  suggested_order_qty: number;
}

export interface InventoryReportResponse {
  data: InventoryReportRow[];
  summary: { total_items: number; below_min: number; above_max: number; start_date: string; end_date: string };
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export interface InventoryReportFilters {
  start_date?: string;
  end_date?: string;
  location_id?: string;
  search?: string;
  page?: number;
  limit?: number;
}

function qs(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

// Server caps a page at 200 rows; the export walks every page.
const EXPORT_PAGE_SIZE = 200;

export const inventoryReportService = {
  report(filters: InventoryReportFilters) {
    return apiClientEnvelope<InventoryReportResponse>(`/products/reports/inventory${qs({ ...filters })}`);
  },

  async reportAll(filters: Omit<InventoryReportFilters, 'page' | 'limit'>): Promise<InventoryReportRow[]> {
    const rows: InventoryReportRow[] = [];
    for (let page = 1; ; page++) {
      const res = await this.report({ ...filters, page, limit: EXPORT_PAGE_SIZE });
      rows.push(...res.data);
      if (page >= res.meta.totalPages) return rows;
    }
  },

  setStockLevels(productId: string, body: { min_stock?: number; max_stock?: number | null }) {
    return apiClient<{ product_id: string; min_stock: number; max_stock: number | null }>(
      `/products/${productId}/stock-levels`,
      { method: 'PATCH', body },
    );
  },
};
